import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { AdminGlassPanel } from "./AdminGlassPanel";
import { useAppDialogs } from "@/components/ui/app-dialogs";

interface Connection {
  status: "connected" | "disconnected" | "needs_reconnect";
  authMode: string | null;
  connectedAt: string | null;
  updatedAt: string | null;
  accountLabel: string | null;
}

interface BillingStatus {
  connection: Connection;
  appConfigured?: boolean;
  envTokenConfigured?: boolean;
  clientIdConfigured?: boolean;
  clientSecretConfigured?: boolean;
  platformCredentialValid?: boolean;
  recurringAvailable?: boolean;
  lastValidatedAt?: string | null;
  activeRecurringCount?: number;
}

export function SaasMpBillingPanel() {
  const [connection, setConnection] = useState<Connection | null>(null);
  const { confirm } = useAppDialogs();
  const [appConfigured, setAppConfigured] = useState(false);
  const [envTokenConfigured, setEnvTokenConfigured] = useState(false);
  const [clientIdConfigured, setClientIdConfigured] = useState(false);
  const [clientSecretConfigured, setClientSecretConfigured] = useState(false);
  const [platformCredentialValid, setPlatformCredentialValid] = useState(false);
  const [recurringAvailable, setRecurringAvailable] = useState(false);
  const [lastValidatedAt, setLastValidatedAt] = useState<string | null>(null);
  const [activeRecurringCount, setActiveRecurringCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGetJson<BillingStatus>("/api/admin/mp-billing");
      setConnection(r.connection);
      setAppConfigured(Boolean(r.appConfigured));
      setEnvTokenConfigured(Boolean(r.envTokenConfigured));
      setClientIdConfigured(Boolean(r.clientIdConfigured));
      setClientSecretConfigured(Boolean(r.clientSecretConfigured));
      setPlatformCredentialValid(Boolean(r.platformCredentialValid));
      setRecurringAvailable(Boolean(r.recurringAvailable));
      setLastValidatedAt(r.lastValidatedAt ?? null);
      setActiveRecurringCount(Number(r.activeRecurringCount ?? 0));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo cargar Cobros de Structura");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("mp") === "connected") toast.success("Mercado Pago de Structura conectado");
    if (q.get("mp") === "error") toast.error("No se pudo conectar Mercado Pago");
  }, []);

  const post = async (body: Record<string, unknown>) =>
    apiSendJson<Record<string, unknown>>("/api/admin/mp-billing", "POST", body);

  const connect = async () => {
    if (busy != null) return;
    setBusy("connect");
    // No se limpia busy al redirigir: reactivar el botón mientras el navegador viaja a MP permite
    // clics repetidos que disparan varios authorize y Mercado Libre responde 429 (local_rate_limited).
    try {
      const r = await post({ action: "connect" }) as { authorizeUrl?: string; error?: string };
      if (r.authorizeUrl) {
        window.location.href = r.authorizeUrl;
        return;
      }
      toast.error(r.error ?? "No se pudo iniciar la conexión");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo conectar");
    }
    setBusy(null);
  };

  const clientCredentials = async () => {
    setBusy("cc");
    try {
      await post({ action: "client_credentials" });
      toast.success("Cuenta de Structura conectada");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo conectar");
    } finally {
      setBusy(null);
    }
  };

  const saveApp = async () => {
    setBusy("save");
    try {
      const r = await post({ action: "save_app_credentials", clientId, clientSecret }) as { notice?: string };
      toast.success(r.notice ?? "Credenciales guardadas (runtime, sin deploy)");
      setClientId("");
      setClientSecret("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy("test");
    try {
      const r = await post({ action: "test" }) as { ok?: boolean; label?: string; error?: string };
      if (r.ok) toast.success(r.label ? `Mercado Pago conectado correctamente · ${r.label}` : "Mercado Pago conectado correctamente.");
      else toast.error(r.error ?? "No pudimos validar la conexión.");
    } catch {
      toast.error("No pudimos validar la conexión.");
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!(await confirm({ title: "¿Desconectar la cuenta de cobros de Structura?", destructive: true, confirmLabel: "Desconectar" }))) return;
    setBusy("disconnect");
    try {
      await post({ action: "disconnect" });
      toast.success("Desconectada");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo desconectar");
    } finally {
      setBusy(null);
    }
  };

  const status = connection?.status ?? "disconnected";
  const hasSomeCredential = appConfigured || envTokenConfigured;
  const statusExplanation =
    status === "connected"
      ? "Mercado Pago está autorizado para cobrar las suscripciones de Structura."
      : status === "needs_reconnect"
      ? "La conexión anterior dejó de ser válida (token expirado o revocado). Volvé a conectar para retomar los cobros."
      : hasSomeCredential
      ? "Hay credenciales configuradas, pero todavía no hay una cuenta de Mercado Pago verificada. Probá la conexión o conectá una cuenta."
      : "Mercado Pago no está conectado. Configurá las credenciales para habilitar los cobros.";

  return (
    <AdminGlassPanel
      title="Cobros de Structura"
      description="Cuenta utilizada para cobrar las suscripciones SaaS. No es la misma que usa cada comercio para cobrar a sus clientes."
    >
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-border/60 bg-secondary/15 p-3">
            <p className="text-sm font-medium">Mercado Pago</p>
            <p className="text-xs text-muted-foreground">Cuenta utilizada para cobrar las suscripciones</p>
            <p className="mt-1 text-sm">
              {status === "connected" ? "🟢 Conectada" : status === "needs_reconnect" ? "🟠 Necesita reconexión" : "🔴 No conectada"}
            </p>
            <p className="mt-1 text-sm">
              {recurringAvailable
                ? "🟢 Billing recurrente disponible"
                : hasSomeCredential
                ? "🟡 Credenciales configuradas, sin verificar"
                : "🔴 Configuración incompleta"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{statusExplanation}</p>
            {connection?.accountLabel ? <p className="text-xs text-muted-foreground">{connection.accountLabel}</p> : null}
            <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
              <li>Client ID: {clientIdConfigured ? "configurado" : "faltante"}</li>
              <li>Client Secret: {clientSecretConfigured ? "configurado" : "faltante"}</li>
              <li>Credencial de plataforma: {platformCredentialValid ? "válida" : "no conectada"}</li>
              <li>Suscripciones activas: {activeRecurringCount}</li>
              <li>Última validación: {lastValidatedAt ? new Date(lastValidatedAt).toLocaleString("es-AR") : "sin validar"}</li>
            </ul>
            <div className="mt-3 flex flex-wrap gap-2">
              {status === "connected" ? (
                <>
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy != null} onClick={() => void test()}>Probar conexión</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy != null} onClick={() => void disconnect()}>Desconectar</Button>
                </>
              ) : (
                <>
                  {appConfigured ? (
                    <>
                      <Button size="sm" className="h-8 text-xs" disabled={busy != null} onClick={() => void connect()}>
                        Conectar Mercado Pago
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy != null} onClick={() => void clientCredentials()}>
                        Usar cuenta propia
                      </Button>
                    </>
                  ) : null}
                  {envTokenConfigured ? (
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy != null} onClick={() => void test()}>
                      Probar conexión
                    </Button>
                  ) : null}
                  {!hasSomeCredential ? (
                    <p className="text-xs text-muted-foreground">
                      Configurá el Client ID/Secret de la app abajo, o la variable de entorno MP_ACCESS_TOKEN.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium">App de Mercado Pago (runtime)</p>
            <p className="text-[11px] text-muted-foreground">
              APP_ID numérico de la aplicación en developers.mercadopago.com (no es un email ni un usuario). Se guarda cifrado y aplica de inmediato, sin deploy.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label className="text-[11px]">Client ID</Label>
                <Input className="mt-0.5 h-8 text-xs" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder={appConfigured ? "••••••••" : "APP_ID numérico"} autoComplete="off" />
              </div>
              <div>
                <Label className="text-[11px]">Client Secret</Label>
                <Input className="mt-0.5 h-8 text-xs" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={appConfigured ? "••••••••" : ""} autoComplete="off" />
              </div>
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy != null} onClick={() => void saveApp()}>Guardar app</Button>
          </div>
        </div>
      )}
    </AdminGlassPanel>
  );
}
